import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, Crown, Sparkles, Star, ShieldCheck, ArrowRight, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const TIERS = [
  {
    id: "Community Sponsor",
    price: 250,
    icon: ShieldCheck,
    tagline: "Show your support",
    benefits: [
      "Website mention",
      "Listing in the official SCU program",
      "Sponsor thank-you recognition",
    ],
  },
  {
    id: "Utes Supporter",
    price: 500,
    icon: Star,
    tagline: "Standard sponsor package",
    benefits: [
      "Website recognition",
      "Program listing",
      "Social media features",
      "Game-day recognition",
    ],
  },
  {
    id: "United Partner",
    price: 750,
    icon: Sparkles,
    recommended: true,
    tagline: "Most value — recommended",
    benefits: [
      "Everything in Utes Supporter",
      "Multiple social media features throughout the season",
      "Dedicated business spotlight post",
      "Field banner recognition",
      "Enhanced game-day recognition",
      "Featured placement in the SCU program",
    ],
  },
  {
    id: "Founding Partner",
    price: 1000,
    icon: Crown,
    prestige: true,
    tagline: "Permanent Inaugural-Season recognition",
    benefits: [
      "Everything in United Partner",
      "Official \"Founding Partner of St. Charles United — Inaugural 2027 Season\" designation",
      "Permanent Founding Partner recognition on the SCU homepage",
      "Premium field banner recognition",
      "Featured social media recognition throughout the season",
      "Premium game-day recognition",
      "Featured placement in the SCU program",
      "Founding Partner T-Shirt",
    ],
  },
];

const SCU_EMAIL = "StCharlesUnited26@gmail.com";

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({
    business_name: "", contact_person: "", email: "", phone: "",
    selected_tier: "United Partner", contribution_amount: "",
    website_or_social: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  useEffect(() => { api.get("/sponsors").then(r => setSponsors(r.data)).catch(() => {}); }, []);

  const selectTier = (tier) => {
    setForm((f) => ({ ...f, selected_tier: tier.id, contribution_amount: tier.price ? String(tier.price) : f.contribution_amount }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sponsor-inquiries", { ...form, sponsorship_interest: form.selected_tier });
      toast.success("Thanks — SCU will follow up about your sponsorship soon.");
      setForm({ business_name: "", contact_person: "", email: "", phone: "", selected_tier: "United Partner", contribution_amount: "", website_or_social: "", notes: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="sponsors-page">
      {/* HERO */}
      <section className="bg-scu-obsidian text-white">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
          <div className="text-xs uppercase tracking-[0.35em] text-scu-electric">Partnership</div>
          <h1 className="mt-3 font-bebas text-6xl md:text-8xl tracking-wider">2027 SCU Sponsorship<br />Opportunities</h1>
          <p className="mt-6 max-w-3xl text-white/75 text-lg leading-relaxed">
            Businesses and community organizations can support St. Charles United while helping provide youth athletes
            with quality equipment, programming, opportunities and a positive competitive experience.
            Every sponsorship keeps costs down for families and puts your brand in front of the parish.
          </p>
        </div>
        <div className="stripe-divider" />
      </section>

      {/* TIER CARDS */}
      <section className="mx-auto max-w-[1400px] px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => {
            const Icon = t.icon;
            const highlight = t.recommended;
            const prestige = t.prestige;
            return (
              <Card
                key={t.id}
                data-testid={`tier-${t.id.toLowerCase().replaceAll(" ","-")}`}
                className={[
                  "relative flex flex-col overflow-hidden rounded-md transition-transform duration-200 hover:-translate-y-1",
                  prestige
                    ? "bg-gradient-to-b from-scu-obsidian via-scu-charcoal to-scu-purple text-white border-scu-electric shadow-[0_20px_60px_-20px_rgba(75,31,158,0.6)]"
                    : highlight
                    ? "bg-scu-purple text-white border-scu-electric scu-purple-glow"
                    : "bg-white border-scu-smoke/20",
                ].join(" ")}
              >
                {highlight && !prestige && (
                  <div className="absolute top-3 right-3 bg-white text-scu-purple text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                    Recommended
                  </div>
                )}
                {prestige && (
                  <div className="absolute top-3 right-3 bg-scu-electric text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                    Founding Partner
                  </div>
                )}

                <div className="p-6 pb-4">
                  <Icon className={`h-8 w-8 ${prestige || highlight ? "text-white" : "text-scu-purple"}`} />
                  <div className={`mt-4 font-bebas text-2xl md:text-3xl tracking-wide ${prestige || highlight ? "text-white" : "text-scu-obsidian"}`}>
                    {t.id}
                  </div>
                  <div className={`mt-1 text-xs uppercase tracking-widest ${prestige || highlight ? "text-white/70" : "text-scu-smoke"}`}>
                    {t.tagline}
                  </div>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className={`font-bebas text-5xl tracking-wide ${prestige || highlight ? "text-white" : "text-scu-obsidian"}`}>${t.price.toLocaleString()}</span>
                    <span className={`text-xs uppercase tracking-widest ${prestige || highlight ? "text-white/60" : "text-scu-smoke"}`}>/ 2027 season</span>
                  </div>
                </div>

                <div className={`px-6 py-4 flex-1 ${prestige || highlight ? "border-t border-white/10" : "border-t border-scu-smoke/15"}`}>
                  <ul className="space-y-2.5 text-sm">
                    {t.benefits.map((b) => (
                      <li key={b} className="flex gap-2">
                        <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${prestige || highlight ? "text-white" : "text-scu-purple"}`} />
                        <span className={prestige || highlight ? "text-white/90" : "text-scu-smoke"}>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 pt-2">
                  <Button
                    data-testid={`tier-cta-${t.id.toLowerCase().replaceAll(" ","-")}`}
                    onClick={() => selectTier(t)}
                    className={
                      prestige || highlight
                        ? "w-full bg-white text-scu-purple hover:bg-scu-electric hover:text-white font-bold uppercase tracking-widest"
                        : "w-full bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest"
                    }
                  >
                    Become a Sponsor <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>

                {prestige && (
                  <div className="px-6 pb-6">
                    <p className="text-[11px] uppercase tracking-widest text-white/70 leading-relaxed">
                      Founding Partners remain <b className="text-white">permanently recognized</b> as businesses that helped launch St. Charles United.
                    </p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Custom / higher contribution */}
        <div className="mt-10 border border-scu-purple/30 bg-scu-purple/5 rounded-md p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="custom-contribution">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Looking to make an even greater impact?</div>
            <h3 className="mt-2 font-bebas text-3xl md:text-4xl tracking-wide text-scu-obsidian">Customized Sponsorship Opportunities</h3>
            <p className="mt-2 text-scu-smoke max-w-3xl">
              St. Charles United gratefully welcomes contributions exceeding our $1,000 Founding Partner sponsorship level.
              Businesses, organizations and individuals interested in contributing more than $1,000 may contact SCU to
              discuss customized sponsorship opportunities, recognition and partnership benefits tailored to their level of support.
            </p>
          </div>
          <Button
            data-testid="custom-cta"
            onClick={() => selectTier({ id: "Custom / Above $1,000", price: 1000 })}
            className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-12 px-6 flex-shrink-0"
          >
            Discuss Custom Package <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Founding Partners — hero call-out for tier-1 sponsors. Renders only when at least one exists. */}
      {sponsors.some((s) => (s.level || "").toLowerCase() === "founding partner") && (
        <section className="relative bg-scu-obsidian text-white overflow-hidden" data-testid="founding-partners-section">
          <div className="scanlines absolute inset-0 opacity-20 pointer-events-none" />
          <div className="relative mx-auto max-w-[1400px] px-4 lg:px-8 py-20">
            <div className="flex items-center gap-3">
              <Crown className="h-5 w-5 text-scu-electric" />
              <div className="text-xs uppercase tracking-[0.35em] text-scu-electric font-bold">Founding Partners</div>
            </div>
            <h2 className="mt-3 font-bebas text-5xl md:text-6xl tracking-wide leading-none">The First To Believe.</h2>
            <p className="mt-4 max-w-3xl text-white/70 text-lg leading-relaxed">
              Founding Partners committed to St. Charles United during our inaugural build — investing in the athletes,
              families and community that make SCU possible. Their support powers every uniform, every practice, and every kid on the field.
            </p>
            <div className="mt-10 grid md:grid-cols-2 gap-5">
              {sponsors.filter((s) => (s.level || "").toLowerCase() === "founding partner").map((s) => (
                <div
                  key={s.id}
                  data-testid={`founding-card-${s.id}`}
                  className="group relative border border-scu-electric/25 rounded-md p-6 md:p-7 bg-white/[0.03] backdrop-blur-md hover:border-scu-electric transition-colors"
                >
                  <div className="flex flex-col sm:flex-row items-start gap-5">
                    <div className="flex flex-col items-center sm:items-start shrink-0 w-full sm:w-auto">
                      <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-md bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt={s.name} className="max-h-full max-w-full object-contain" />
                        ) : (
                          <Crown className="h-8 w-8 text-scu-electric" />
                        )}
                      </div>
                      {s.quote && (
                        <blockquote
                          data-testid={`founding-quote-${s.id}`}
                          className="mt-4 w-full max-w-[200px] sm:max-w-[220px] text-center sm:text-left border-l-2 border-scu-electric/60 pl-3 text-white/85 italic text-xs leading-snug"
                        >
                          “{s.quote}”
                        </blockquote>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bebas text-3xl md:text-4xl tracking-wide leading-none">{s.name}</div>
                      <p className="mt-3 text-white/80 text-sm leading-relaxed">
                        {s.story || "Proud Founding Partner of the St. Charles United Utes — supporting our athletes, families and inaugural 2027 season."}
                      </p>
                      {s.website && (
                        <a
                          href={s.website}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-scu-electric hover:text-white transition-colors"
                        >
                          Visit business <ArrowRight className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="h-[2px] w-full bg-gradient-to-r from-scu-electric via-scu-purple to-scu-electric" />
        </section>
      )}

      {/* Current Sponsors */}
      <section className="bg-scu-off border-y border-scu-smoke/15 py-16">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Our Sponsors</div>
          <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Powered By Community</h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {(sponsors.length ? sponsors : Array.from({ length: 6 }, (_, i) => ({ id: `open-${i}`, name: "YOUR BUSINESS HERE", level: "Open", open: true }))).map((s) => (
              <div
                key={s.id}
                data-testid={`sponsor-${s.id}`}
                className={`border rounded-md min-h-56 flex flex-col items-center justify-start px-3 py-4 overflow-hidden ${s.open ? "border-dashed border-scu-smoke/30 bg-white text-scu-smoke/70" : "border-scu-smoke/20 bg-white hover:border-scu-purple transition-colors"}`}
              >
                {s.logo_url ? (
                  <>
                    <img src={s.logo_url} alt={s.name} className="max-h-40 max-w-[95%] object-contain" />
                    <div className="mt-3 text-sm font-bold text-center text-scu-obsidian leading-tight">{s.name}</div>
                    {s.quote && (
                      <blockquote data-testid={`sponsor-quote-${s.id}`} className="mt-2 border-l-2 border-scu-purple/60 pl-2 text-[11px] italic text-scu-smoke leading-snug text-center">
                        “{s.quote}”
                      </blockquote>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`text-[10px] uppercase tracking-widest font-bold ${s.open ? "text-scu-smoke/60" : "text-scu-purple"}`}>{s.level}</div>
                    <div className={`mt-1 text-sm font-bold text-center ${s.open ? "text-scu-smoke/70" : "text-scu-obsidian"}`}>{s.name}</div>
                    {!s.open && s.quote && (
                      <blockquote data-testid={`sponsor-quote-${s.id}`} className="mt-2 border-l-2 border-scu-purple/60 pl-2 text-[11px] italic text-scu-smoke leading-snug text-center">
                        “{s.quote}”
                      </blockquote>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section ref={formRef} className="mx-auto max-w-3xl px-4 lg:px-8 py-16" id="sponsor-form">
        <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Become a Sponsor</div>
        <h2 className="mt-3 font-bebas text-5xl tracking-wide text-scu-obsidian">Sponsorship Inquiry</h2>
        <p className="mt-2 text-scu-smoke">Complete the form and SCU will reach out to finalize your 2027 sponsorship.</p>

        <form onSubmit={submit} className="mt-8 bg-white border border-scu-smoke/15 rounded-md p-6 md:p-8 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <F label="Business or organization name" required>
              <Input data-testid="sp-biz" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
            </F>
            <F label="Contact person" required>
              <Input data-testid="sp-person" required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </F>
            <F label="Email" required>
              <Input data-testid="sp-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </F>
            <F label="Phone" required>
              <Input data-testid="sp-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </F>
            <F label="Selected sponsorship tier" required>
              <Select value={form.selected_tier} onValueChange={(v) => setForm({ ...form, selected_tier: v })}>
                <SelectTrigger data-testid="sp-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => <SelectItem key={t.id} value={t.id}>{t.id} — ${t.price.toLocaleString()}</SelectItem>)}
                  <SelectItem value="Custom / Above $1,000">Custom / Above $1,000</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Contribution amount (USD)">
              <Input data-testid="sp-amount" placeholder="e.g. 750" value={form.contribution_amount} onChange={(e) => setForm({ ...form, contribution_amount: e.target.value })} />
            </F>
            <div className="md:col-span-2">
              <F label="Business website or social media link">
                <Input data-testid="sp-web" placeholder="https://…" value={form.website_or_social} onChange={(e) => setForm({ ...form, website_or_social: e.target.value })} />
              </F>
            </div>
            <div className="md:col-span-2">
              <F label="Notes or special requests">
                <Textarea data-testid="sp-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </F>
            </div>
          </div>
          <Button data-testid="sp-submit" type="submit" disabled={loading} className="bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest h-11 px-6">
            {loading ? "Sending…" : "Send Sponsorship Inquiry"}
          </Button>
          <div className="text-sm text-scu-smoke flex items-center gap-2 pt-2">
            <Mail className="h-4 w-4 text-scu-purple" />
            Prefer email? Reach SCU at <a data-testid="sp-email-link" href={`mailto:${SCU_EMAIL}`} className="text-scu-purple hover:text-scu-electric font-bold break-all">{SCU_EMAIL}</a>
          </div>
        </form>
      </section>
    </div>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">
        {label} {required && <span className="text-scu-purple">*</span>}
      </Label>
      {children}
    </div>
  );
}
